import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { FiImage } from "react-icons/fi";
import { useMutation } from "@apollo/client";
import { CREATE_PRODUCT_CATEGORIES } from "../../../Utils/Mutation";
import Loader from "../../Common/Loader";
import { useSnackbarHook } from "../../../hooks/useSnackbarHook";
import { uploadSingleImageToCloudinary } from "../../../Utils/productEditHelpers";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 540,
    flexShrink: 0,
    p: 4,
  },
  drawerPaper: {
    width: 540,
    p: 4,
  },
  toolbar: theme.mixins.toolbar,
}));

const initialFormState = {
  name: "",
  description: "",
  isActive: true,
  imageUrl: "",
};

const CreateCategory = ({ open, onClose, action }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const createdPreviewUrlRef = useRef(null);

  const [formState, setFormState] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState("");

  const { handleClick } = useSnackbarHook();

  const [createCategory, { loading: isCreating }] = useMutation(
    CREATE_PRODUCT_CATEGORIES,
  );

  useEffect(() => {
    if (!open) {
      setFormState(initialFormState);
      setSelectedFile(null);
      setPreviewUrl("");
      setFormError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (createdPreviewUrlRef.current) {
        URL.revokeObjectURL(createdPreviewUrlRef.current);
      }
    };
  }, []);

  const isSubmitDisabled = useMemo(
    () =>
      isUploading ||
      isCreating ||
      !formState.name.trim() ||
      !formState.imageUrl,
    [formState.imageUrl, formState.name, isCreating, isUploading],
  );

  const updateFormField = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFormError("Please select a valid image file.");
      handleClick("Please select a valid image file.", "error");
      return;
    }

    setFormError("");
    setSelectedFile(file);

    if (createdPreviewUrlRef.current) {
      URL.revokeObjectURL(createdPreviewUrlRef.current);
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    createdPreviewUrlRef.current = nextPreviewUrl;
    setPreviewUrl(nextPreviewUrl);

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadSingleImageToCloudinary(file);
      updateFormField("imageUrl", uploadedUrl);
      handleClick("Image uploaded successfully", "success");
    } catch (err) {
      setFormError(err?.message || "Image upload failed.");
      updateFormField("imageUrl", "");
      handleClick(err?.message || "Image upload failed.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateCategory = async () => {
    setFormError("");

    if (!formState.name.trim()) {
      setFormError("Category name is required.");
      return;
    }

    if (!formState.imageUrl) {
      setFormError("Please upload a category image before submitting.");
      return;
    }

    try {
      await createCategory({
        variables: {
          name: formState.name.trim(),
          imageUrl: formState.imageUrl,
          isActive: formState.isActive,
          description: formState.description.trim() || null,
        },
      });

      handleClick("Category created successfully", "success");
      action?.();
      onClose();
    } catch (err) {
      const message = err?.message || "Failed to create category.";
      setFormError(message);
      handleClick(message, "error");
    }
  };

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      {(isCreating || isUploading) && <Loader />}
      <Box sx={{ px: 4, py: 5 }} className={classes.toolbar}>
        <Box className="flex justify-between gap-x-6">
          <div>
            <Typography
              sx={{
                fontFamily: "Lato",
                color: "#0D0D0D",
                fontSize: "18px",
                fontWeight: 900,
              }}
            >
              Create Category
            </Typography>
            <Typography sx={{ fontSize: "12px" }}>
              Define a new label to group related items and improve visibility
              across your system.
            </Typography>
          </div>
          <IconButton onClick={onClose}>
            <IoMdClose style={{ fontSize: "25px", color: "#000" }} />
          </IconButton>
        </Box>

        <Box sx={{ mt: 4, display: "grid", rowGap: 3 }}>
          <TextField
            label="Category Name *"
            fullWidth
            value={formState.name}
            onChange={(e) => updateFormField("name", e.target.value)}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={4}
            value={formState.description}
            onChange={(e) => updateFormField("description", e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formState.isActive}
                onChange={(e) => updateFormField("isActive", e.target.checked)}
              />
            }
            label="Active category"
          />

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Category Image *</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={handleFileSelection}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px dashed #C9C9C9",
                borderRadius: "16px",
                p: 3,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                rowGap: 1,
                bgcolor: "#FAFAFA",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#7B408C",
                  bgcolor: "#F8F3FA",
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#F3EAF6",
                }}
              >
                {isUploading ? (
                  <CircularProgress size={22} sx={{ color: "#7B408C" }} />
                ) : (
                  <FiImage style={{ color: "#7B408C", fontSize: 20 }} />
                )}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                {isUploading ? "Uploading image..." : "Click to upload image"}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                PNG, JPG, or WEBP
              </Typography>
            </Box>

            {(previewUrl || formState.imageUrl) && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 1 }}>
                  Preview
                </Typography>
                <Box
                  component="img"
                  src={previewUrl || formState.imageUrl}
                  alt={selectedFile?.name || formState.name || "Category preview"}
                  sx={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: "12px",
                    border: "1px solid #E5E5E5",
                  }}
                />
              </Box>
            )}
          </Box>

          {formError && (
            <Typography sx={{ color: "#D32F2F", fontSize: "13px" }}>
              {formError}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 1 }}
            onClick={handleCreateCategory}
            disabled={isSubmitDisabled}
          >
            {isUploading ? "Uploading Image..." : isCreating ? "Creating..." : "Create Category"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreateCategory;
